import React from "react";
import {
  Building2,
  FileCheck2,
  MapPinned,
  type LucideIcon,
} from "lucide-react";
import { FaArrowRight } from "react-icons/fa";
import { useHistory } from "react-router-dom";

type IntegrationCapability = {
  title: string;
  description: string;
  icon: LucideIcon;
};

const integrationCapabilities: IntegrationCapability[] = [
  {
    title: "Facilities & service requests",
    description:
      "Register facilities, assign contact persons, and submit maintenance requests directly from your own workflows.",
    icon: Building2,
  },
  {
    title: "Assignments & visit tracking",
    description:
      "Track inspections, work visits, and request progress as each job moves from intake to completion.",
    icon: MapPinned,
  },
  {
    title: "Quotations, invoices & webhooks",
    description:
      "Review quotations, manage invoice workflows, and receive real-time webhook notifications as request activity changes.",
    icon: FileCheck2,
  },
];

interface ApiIntegrationSectionProps {
  onRequestAccess: () => void;
}

export const ApiIntegrationSection: React.FC<ApiIntegrationSectionProps> = ({
  onRequestAccess,
}) => {
  const history = useHistory();

  return (
    <section className="w-full border-y-[0.5px] border-[#121212] bg-white py-10 lg:py-16">
      <div className="mx-auto w-full max-w-[1440px] px-6 md:px-16">
        <div className="mb-8 md:mb-12 max-w-[920px]">
          <p className="font-[Inter] text-xs font-medium uppercase tracking-[0.24em] text-[#ED6E5C] md:text-sm">
            For developers & product teams
          </p>
          <h2 className="mt-4 font-[AeonikPro] text-2xl font-medium leading-[1.2] text-[#040A0D] md:text-[40px]">
            Build with LaborHack
          </h2>
          <p className="mt-4 font-[Inter] text-base leading-[1.875] text-[#464646] md:text-xl md:leading-[1.6]">
            Integrate facilities management and maintenance services directly
            into your systems with the LaborHack API. Create facilities,
            submit service requests, track assignments, and manage
            post-service operations programmatically.
          </p>
        </div>

        <div className="border-t-[0.5px] border-[#121212] pt-8 md:pt-12">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-12">
            {integrationCapabilities.map(({ title, description, icon: Icon }) => (
              <div
                key={title}
                className="flex flex-col items-start gap-3 md:gap-4 lg:w-[88%]"
              >
                <div className="flex h-16 items-start justify-start lg:h-20">
                  <Icon
                    className="h-12 w-12 text-[#212121] lg:h-14 lg:w-14"
                    strokeWidth={1.25}
                    absoluteStrokeWidth
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="font-[Inter] text-lg font-medium leading-[1.318] text-[#212121] md:text-[22px]">
                    {title}
                  </h3>
                  <p className="max-w-[416px] font-[Inter] text-sm leading-[1.875] text-[#464646] md:text-base">
                    {description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 border-t-[0.5px] border-[#121212] pt-8 md:mt-12 md:pt-12">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <button
                onClick={onRequestAccess}
                className="flex h-12 items-center justify-center gap-2 bg-[#ED6E5C] px-6 py-3 font-inter text-base font-medium text-[#FEFEFE] transition-all duration-200 ease-in-out hover:-translate-y-0.5 hover:bg-[#E55D4B] hover:shadow-lg active:translate-y-0 md:h-16 md:px-10 md:py-5 md:text-lg"
              >
                Request API access
                <FaArrowRight size="14" />
              </button>

              <button
                onClick={() => history.push("/api-docs")}
                className="flex h-12 items-center justify-center border border-[#D9D9D9] bg-transparent px-6 py-3 font-inter text-base font-medium text-[#153243] transition-all duration-200 ease-in-out hover:-translate-y-0.5 hover:bg-[rgba(21,50,67,0.06)] active:translate-y-0 md:h-16 md:px-10 md:py-5 md:text-lg"
              >
                View API documentation
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
